import React, { useEffect, useRef } from 'react';
import { array, number, string, bool, func } from 'prop-types';
import classNames from 'classnames';
import ImageGallery from 'react-image-gallery';
import { FormattedMessage } from '../../../util/reactIntl';

import { Modal, IconArrowHead, IconDownload } from '../../../components';

import css from './FullscreenImages.module.css';
import './image-gallery.css';

const FullscreenImages = props => {
  const {
    id,
    rootClassName,
    className,
    images,
    isModalOpen,
    onClose,
    selectedIndex,
    alreadyFormatted,
    disableDownload,
    thumbnailClassName,
    rootGalleryClassName,
    wrapperFullscreen,
    customPortal = false,
    onManageDisableScrolling,
  } = props;
  const galleryRef = useRef(null);
  const activeDownloadButtonRef = useRef(null);

  useEffect(() => {
    if (isModalOpen && galleryRef.current && typeof selectedIndex === 'number') {
      galleryRef.current.slideToIndex(selectedIndex);
    }
  }, [isModalOpen, images]);

  const classes = classNames(rootClassName || css.root, className);

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else {
      console.log('Your browser does not support fullscreen mode');
    }
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          onClick();
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };

  const downloadImage = async () => {
    try {
      const currentIndex = galleryRef.current?.getCurrentIndex?.();
      const currentItem = items?.[currentIndex];

      if (!currentItem?.original) return;

      const response = await fetch(currentItem.original, { mode: 'cors' });
      if (!response.ok) throw new Error('Network error');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = currentItem.name || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderItem = item => {
    return (
      <div className={wrapperFullscreen ? wrapperFullscreen : css.itemWrapperFullscreen}>
        {!disableDownload && (
          <button
            ref={el => {
              if (el) activeDownloadButtonRef.current = el;
            }}
            style={{ visibility: 'hidden' }}
            className={css.downloadBtn}
            onClick={downloadImage}
          >
            <IconDownload className={css.downloadIcon} />
            <FormattedMessage id="SendMessageForm.downloadPictureButton" />
          </button>
        )}
        <div className={css.itemContainer}>
          <div className={css.item} style={{ backgroundImage: `url(${item?.original})` }} />
        </div>
      </div>
    );
  };

  const items = alreadyFormatted
    ? images
    : images?.map(image => ({
        original: image?.url,
        thumbnail: image?.url,
        name: image?.name,
      }));

  return (
    <Modal
      id={id}
      containerClassName={css.modalContainer}
      isOpen={isModalOpen}
      disableScrollingAfterClose={true}
      onClose={() => {
        onClose();
        exitFullscreen();
      }}
      // customPortal={customPortal}
      // closeButtonMessage={' '}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling ? onManageDisableScrolling : () => {}}
      scrollLayerClassName={css.scrollLayer}
    >
      <div className={rootGalleryClassName ? rootGalleryClassName : css.gallery}>
        {!disableDownload && (
          <button
            className={css.downloadBtn}
            onClick={() => {
              activeDownloadButtonRef.current?.click();
            }}
          >
            <IconDownload className={css.downloadIcon} />
            <FormattedMessage id="SendMessageForm.downloadPictureButton" />
          </button>
        )}
        <ImageGallery
          ref={galleryRef}
          items={items}
          renderItem={renderItem}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          showPlayButton={false}
          renderFullscreenButton={() => null}
          // slideToIndex={0}
          renderThumbInner={image => (
            <div
              key={image?.thumbnail}
              className={thumbnailClassName ? thumbnailClassName : css.galleryThumbnail}
              style={{ backgroundImage: `url(${image?.thumbnail})` }}
            ></div>
          )}
        />
      </div>
    </Modal>
  );
};

FullscreenImages.defaultProps = {
  rootClassName: null,
  className: null,
  // images: [],
  isModalOpen: false,
  onClose: () => null,
  selectedIndex: 0,
  onManageDisableScrolling: () => null,
  alreadyFormatted: false,
};

FullscreenImages.propTypes = {
  rootClassName: string,
  className: string,
  images: array,
  isModalOpen: bool,
  onClose: func.isRequired,
  selectedIndex: number,
  onManageDisableScrolling: func.isRequired,
  alreadyFormatted: bool,
};

export default FullscreenImages;
